import React, { useRef, useEffect } from 'react';
import { formatFeedbackTime } from '../../lib/feedbackPosts/formatTime';
import type { Notification } from '../../lib/notifications/types';

interface NotificationCenterProps {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  onMarkAsRead: (notification: Notification) => Promise<void>;
  onNavigate: (page: string, id?: string | null) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
}

function NotificationItem({
  notification,
  onMarkAsRead,
  onNavigate,
  onClose,
}: {
  notification: Notification;
  onMarkAsRead: (notification: Notification) => Promise<void>;
  onNavigate: (page: string, id?: string | null) => void;
  onClose: () => void;
}) {
  const isFeedbackComment = notification.type === 'feedback_comment';
  const handleClick = () => {
    onMarkAsRead(notification);
    if (isFeedbackComment && notification.postId) {
      onNavigate('feedback-feed', notification.postId);
    }
    onClose();
  };

  const label =
    isFeedbackComment
      ? `${notification.actorDisplayName || 'Someone'} commented on your post`
      : 'New activity';

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`
        w-full flex items-start gap-3 px-3 py-2.5 text-left rounded-lg transition-colors
        ${notification.read ? 'bg-transparent' : 'bg-slate-50'}
        hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-ihTeal/30 focus:ring-inset
      `}
    >
      <span className="mt-0.5 flex-shrink-0 h-9 w-9 rounded-full bg-slate-200 overflow-hidden">
        {notification.actorPhotoURL ? (
          <img
            src={notification.actorPhotoURL}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-sm font-semibold text-slate-500">
            {(notification.actorDisplayName || '?').charAt(0)}
          </span>
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        <p className="mt-0.5 text-xs text-slate-600 line-clamp-1">
          {notification.postTitle || 'Your post'}
        </p>
        {notification.previewText && (
          <p className="mt-1 text-xs text-slate-500 line-clamp-2">
            {notification.previewText}
          </p>
        )}
        {notification.createdAt && (
          <p className="mt-1 text-[11px] text-slate-400">
            {formatFeedbackTime(notification.createdAt)}
          </p>
        )}
      </div>
      {!notification.read && (
        <span className="flex-shrink-0 mt-2 w-2 h-2 rounded-full bg-ihTeal" aria-hidden />
      )}
    </button>
  );
}

export default function NotificationCenter({
  notifications,
  unreadCount,
  loading,
  onMarkAsRead,
  onNavigate,
  onClose,
  anchorRef,
}: NotificationCenterProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const anchor = anchorRef?.current;
      const panel = panelRef.current;
      if (
        panel &&
        anchor &&
        !panel.contains(e.target as Node) &&
        !anchor.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [anchorRef, onClose]);

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full z-50 mt-1 w-[360px] max-w-[calc(100vw-24px)] rounded-xl border border-slate-200 bg-white shadow-lg"
      role="dialog"
      aria-label="Notifications"
    >
      <div className="border-b border-slate-100 px-3 py-2.5">
        <h2 className="text-sm font-semibold text-slate-900">Notifications</h2>
        {unreadCount > 0 && (
          <p className="text-xs text-slate-500 mt-0.5">
            {unreadCount} unread
          </p>
        )}
      </div>
      <div className="max-h-[320px] overflow-y-auto py-1">
        {loading && (
          <div className="px-4 py-8 text-center text-sm text-slate-500">
            Loading…
          </div>
        )}
        {!loading && notifications.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-slate-500">
            No notifications yet
          </div>
        )}
        {!loading && notifications.length > 0 && (
          <ul className="space-y-0.5 px-2">
            {notifications.map((n) => (
              <li key={n.id}>
                <NotificationItem
                  notification={n}
                  onMarkAsRead={onMarkAsRead}
                  onNavigate={onNavigate}
                  onClose={onClose}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
